import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getCurrentUser } from '../../utils/auth';
import { makeStyles, MuiThemeProvider } from '@material-ui/core/styles';
import { OrderList } from './OrderList';
import { HeaderContent } from '../../components/molecules/HeaderContent';
import { Button, CircularProgress, Grid } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { FormattedMessage, useIntl } from 'react-intl';
import { CREATE_WORK_ORDER_PERMISSIONS } from '../../router';
import { lightThemeV2 } from '../../settings/themes/lightThemeV2';
import { WorkOrderModal } from './WorkOrderModal';
import { useState } from 'react';
import { BodyContent } from '../../components/molecules/BodyContent';
import { WideContainer } from '../../components/atoms/WideContainer';
import { useCreateWorkOrder, useDeleteWorkOrder, useGetWorkOrders, useUpdateWorkOrder } from '../../queries/queryWorkOrder';
import { WorkOrderFilters } from './WorkOrderFilters';

const useStyles = makeStyles({
  loader: {
    padding: 24,
    textAlign: 'center'
  }
});

const RESET_MODAL_STATE = { open: false, orderToEdit: null };

const WorkOrdersComponent = ({ currentUser }) => {
  const classes = useStyles();
  const createWorkOrderDisabled = !CREATE_WORK_ORDER_PERMISSIONS.find(t => t === currentUser.user_type);
  const { messages: intlMessages } = useIntl();

  const [openWorkOrderModal, setOpenWorkOrderModal] = useState({ open: false, orderToEdit: null });
  const [filters, setFilters] = useState({
    page: 1,
    per_page: 25,
    projectId: null,
    type: null,
    status: null,
    query: null
  });

  const { data: workOrdersData = { workOrders: [], meta: {} }, isLoading, refetch, isRefetching } = useGetWorkOrders({
    clientId: currentUser.client_id,
    params: filters
  });
  const { mutate: createWorkOrder } = useCreateWorkOrder({ onSuccess: () => refetch() });
  const { mutate: updateWorkOrder } = useUpdateWorkOrder({ onSuccess: () => refetch() });
  const { mutate: deleteWorkOrder } = useDeleteWorkOrder({ onSuccess: () => refetch() });

  const handleFilterChange = React.useCallback(newFilters => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      page: 1
    }));
  }, []);

  const handlePaginationChange = newPagination => {
    setFilters(prev => ({
      ...prev,
      page: newPagination.page,
      per_page: newPagination.perPage
    }));
  };

  const handleDeleteWorkOrder = workOrderId => {
    deleteWorkOrder({ clientId: currentUser.client_id, id: workOrderId });
  };

  const submitCreateWorkOrder = ({ workOrder }) => {
    createWorkOrder({
      clientId: currentUser.client_id,
      workOrder
    });
  };

  const submitUpdateWorkOrder = ({ workOrder }) => {
    if (!workOrder.id) return;

    updateWorkOrder({ clientId: currentUser.client_id, workOrder, id: workOrder.id });
  };

  const handleCloseModal = () => setOpenWorkOrderModal(RESET_MODAL_STATE);
  const handleOnCreateWorkOrder = () => setOpenWorkOrderModal({ open: true, orderToEdit: null });
  const handleOnEditWorkOrder = workOrder => setOpenWorkOrderModal({ orderToEdit: workOrder, open: true });

  return (
    <MuiThemeProvider theme={lightThemeV2}>
      <HeaderContent title={intlMessages['workOrders.title']}>
        <Button variant="contained" onClick={handleOnCreateWorkOrder} disabled={createWorkOrderDisabled}>
          <AddIcon fontSize="inherit" />
          <FormattedMessage id="workOrders.button.new" />
        </Button>
      </HeaderContent>
      <WorkOrderFilters workOrders={workOrdersData} onFilterChange={handleFilterChange} />
      <BodyContent component={WideContainer}>
        {isLoading || isRefetching ? (
          <Grid item xs={12} className={classes.loader}>
            <CircularProgress />
          </Grid>
        ) : (
          <OrderList
            datasource={workOrdersData}
            editionEnable={!createWorkOrderDisabled}
            onDeleteOrder={handleDeleteWorkOrder}
            onEditWorkOrder={handleOnEditWorkOrder}
            onChangePagination={handlePaginationChange}
          />
        )}
      </BodyContent>
      <WorkOrderModal
        open={openWorkOrderModal.open}
        onClose={handleCloseModal}
        clientId={currentUser.client_id}
        orderToEdit={openWorkOrderModal.orderToEdit}
        onSave={data => {
          if (data.workOrder.id) submitUpdateWorkOrder(data);
          else submitCreateWorkOrder(data);

          handleCloseModal();
        }}
      />
    </MuiThemeProvider>
  );
};

WorkOrdersComponent.propTypes = {
  currentUser: PropTypes.object.isRequired
};

export const WorkOrders = connect(state => ({
  currentUser: getCurrentUser(state)
}))(WorkOrdersComponent);
